import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "../css/EmployeePOBSOBTarget.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";

const StockistPOBSOBTarget = () => {
  const navigate = useNavigate();

  // State for dropdown options
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);

  // State for selected filters
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  // State for table data and UI
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const tableRef = useRef();

  // Month/Year dropdown setup
  const getMonthYearOptions = () => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const currentYear = new Date().getFullYear();
    return months.map((month, index) => ({
      label: `${month} ${currentYear}`,
      value: `${String(index + 1).padStart(2, '0')}-${currentYear}`
    }));
  };

  const monthYearOptions = getMonthYearOptions();
  const [pobMonthYear, setPobMonthYear] = useState(monthYearOptions[new Date().getMonth()].value);

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  // Fetch employees based on state and division
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) return;

    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
      setError("Failed to load employees");
    }
  }, []);

  // Fetch divisions when state changes
  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState) {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() }
          );
          setDivisions([{ id: "All", name: "All" }, ...response.data]);
          setSelectedDivision("All");
        } catch (err) {
          console.error("Error fetching divisions:", err);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([]);
        setSelectedDivision("All");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // Handle state change
  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);

    // Fetch employees based on new state
    fetchEmployees(stateId, "All");
  };

  // Handle division change
  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);

    // Find division name from ID
    const divisionName = divisions.find(d => d.id == divisionId)?.name || "All";
    
    // Fetch employees based on new division
    if (selectedState) {
      fetchEmployees(selectedState, divisionName);
    }
  };

  // Handle employee change
  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    setError(null);
  };

  // Handle month/year change
  const handleMonthYearChange = (value) => {
    setPobMonthYear(value);
    setError(null);
  };

  // Fetch data when filters change
  const handleGoClick = useCallback(async () => {
    if (!selectedState) {
      setError("Please select a state first");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Parse month and year from pobMonthYear (format: "MM-YYYY")
      const [month, year] = pobMonthYear.split('-').map(Number);
      
      let url = `${BASE_URL}/api/employee/employeewithtarget?state_id=${selectedState}&month=${month}&year=${year}`;
        
      // Add division filter if selected
      if (selectedDivision !== "All") {
        const division = divisions.find(d => d.id == selectedDivision);
        if (division?.name) {
          url += `&division=${encodeURIComponent(division.name)}`;
        }
      }
  
      // Add employee filter if selected
      if (selectedEmployee && selectedEmployee !== "All") {
        url += `&employee_id=${selectedEmployee}`;
      }
  
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setTableData(response.data);
      setShowTable(true);
    } catch (err) {
      console.error("Failed to fetch summary", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedState, selectedDivision, selectedEmployee, pobMonthYear, divisions]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, ' ');
  };

  // Handle print functionality
  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=900,height=600");
    if (!printWindow) return;
    
    const tableHTML = tableRef.current.innerHTML;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Table</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 14px;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
            }
            .table-title {
              text-align: center;
              margin-bottom: 15px;
              font-weight: bold;
              font-size: 18px;
            }
          </style>
        </head>
        <body>
          <div class="table-title">
            Stockist POB/SOB Target Report - 
            ${states.find(s => s.id == selectedState)?.name || selectedState} - 
            ${divisions.find(d => d.id == selectedDivision)?.name || selectedDivision} - 
            ${selectedEmployee === "All" ? "All Employees" : 
              employees.find(e => e.id == selectedEmployee)?.first_name + " " + 
              employees.find(e => e.id == selectedEmployee)?.last_name}
          </div>
          ${tableHTML}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Handle export to Excel
  const handleExport = () => {
    import("xlsx").then((XLSX) => {
      const ws = XLSX.utils.table_to_sheet(tableRef.current.querySelector("table"));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "StockistData");
      XLSX.writeFile(wb, "stockist_data.xlsx");
    }).catch(err => {
      console.error("Failed to export to Excel:", err);
      setError("Failed to export data. Please try again.");
    });
  };

  // Get title for the table
  const getTableTitle = () => {
    const stateName = states.find(s => s.id == selectedState)?.name || selectedState;
    const divisionName = divisions.find(d => d.id == selectedDivision)?.name || selectedDivision;
    
    let employeeName = "All Employees";
    if (selectedEmployee !== "All") {
      const employee = employees.find(e => e.id == selectedEmployee);
      employeeName = employee ? `${employee.first_name} ${employee.last_name}` : selectedEmployee;
    }
    
    return `SOB (${stateName})-${divisionName}-${employeeName}`;
  };

  return (
    <div className="employeePOBSOBTarget-main">

      <div className="employeePOBSOBTarget-filter-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700"> Select </span>
          <span className="text-blue-500">Employee</span>
        </h1>

        <div className="employeePOBSOBTarget-filter-row1">
          <label className="employeePOBSOBTarget-label">State:</label>
          <select
            className="employeePOBSOBTarget-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="employeePOBSOBTarget-label">Division:</label>
          <select
            className="employeePOBSOBTarget-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="employeePOBSOBTarget-filter-row2">
          <label className="employeePOBSOBTarget-label">Employee:</label>
          <select
            className="employeePOBSOBTarget-dropdown"
            value={selectedEmployee}
            onChange={(e) => handleEmployeeChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All" 
                  ? "All" 
                  : `${emp.first_name} ${emp.last_name} | ${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name}`
                }
              </option>
            ))}
          </select>

          <label className="employeePOBSOBTarget-label">Month / Year:</label>
          <select
            className="employeePOBSOBTarget-dropdown"
            value={pobMonthYear}
            onChange={(e) => handleMonthYearChange(e.target.value)}
            disabled={isLoading}
          >
            {monthYearOptions.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button 
          className="employeePOBSOBTarget-go-button" 
          onClick={handleGoClick}
          disabled={isLoading || !selectedState}
        >
          {isLoading ? "Loading..." : "GO"}
        </button>

        {error && <div className="employeePOBSOBTarget-error">{error}</div>}
      </div>

      {showTable && (
        <>
          {/* Table container */}
          <div className="employeePOBSOBTarget-table-container">
            <div>
              <h2 style={{ textAlign: 'center' }}>
                {getTableTitle()}
              </h2>
            </div>

            <div className="employeePOBSOBTarget-table-wrapper" ref={tableRef}>
              <table className="employeePOBSOBTarget-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>Emp ID</th>
                    <th>Employee</th>
                    <th>State</th>
                    <th>HQ</th>
                    <th>Designation</th>
                    <th>Division</th>
                    <th>Manager</th>
                    <th>DOJ</th> 
                    <th>DOR</th>      
                    <th>Target<br/>(Employee)</th>
                    <th>POB <br/>Date Wise</th>
                    <th>SOB</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((item, index) => (
                      <tr key={item.stockist_code || index}>
                        <td>{index + 1}</td>
                        <td>{item.employee_id}</td>
                        <td>{item.employee_name}</td>
                        <td>{item.state_name}</td>
                        <td>{item.headquarter_name}</td>
                        <td>{item.employee_designation}</td>
                        <td>{item.employee_division}</td>
                        <td>{item.employee_manager}</td>
                        <td>{formatDate(item.doj)}</td>
                        <td>{formatDate(item.dor)}</td>
                        <td>{item.sob_target}</td>
                        <td>{item.pob_monthly}</td>
                        <td>{item.sob_achieved}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" style={{ textAlign: 'center' }}>
                        No data available for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Buttons outside the table container */}
          <div className="employeePOBSOBTarget-button-group">
            <CustomButton
              onClick={handlePrint}
              label="Print"
              variant="print"
            />
            <CustomButton
              onClick={handleExport}
              label="Export to Excel"
              variant="export"
            />
          </div>
        </>
      )}

    
    </div>
  );
};

export default StockistPOBSOBTarget;